import dem from "./DEMLoadFunc.js";
import distance from "@turf/distance";
import bearing from "@turf/bearing";
import destination from "@turf/destination";
import logger from "./logger";

export default {
  getDistance,
  getBounds,
  getTmEndPoint,
  getEndPoint,
  createCossSectionData,
  getPointEl,
  checkPresent,
};

const options = {
  units: "kilometers",
};

/**
 * ２点間の距離をキロメートル単位で返却
 * @param {*} from 地点１ [緯度、経度]
 * @param {*} to 地点２ [緯度、経度]
 */
function getDistance(from, to) {
  return distance([from[1], from[0]], [to[1], to[0]], options);
}

/**
 * 座標から指定距離の左下座標、右上座標を返却
 * @param {*} point 座標
 * @param {*} distance 距離
 */
function getBounds(point, distance) {
  let p = [point[1], point[0]];
  // lon:north[0] lat:north[1] 形式で取得できる
  let north = destination(p, distance, 0, options).geometry.coordinates;
  let east = destination(p, distance, 90, options).geometry.coordinates;
  let south = destination(p, distance, 180, options).geometry.coordinates;
  let west = destination(p, distance, 270, options).geometry.coordinates;

  // lat:緯度、lon：経度形式で返却
  return {
    maxCoord: [north[1], east[0]],
    minCoord: [south[1], west[0]],
  };
}

/**
 * A点B点取得
 * @param {*} leftBankInfo 左岸堤防高座標
 * @param {*} rightBankInfo 右岸堤防項座標
 * @param {*} rvrInWid 堤内地描画距離
 * @param {*} riverSideMinWidth 堤内地最低幅
 */
function getTmEndPoint(
  leftBankLatLng,
  rightBankLatLng,
  rvrInWid,
  riverSideMinWidth
) {
  let minWidth = riverSideMinWidth / 1000;

  let leftBank = [leftBankLatLng[1], leftBankLatLng[0]];
  let rightBank = [rightBankLatLng[1], rightBankLatLng[0]];

  // 川幅を取得
  let rvrwid = distance(leftBank, rightBank, options);
  // logger.debug("rvrwid:" + rvrwid);
  // logger.debug("minWidth:" + minWidth);

  // 描画範囲の両端（A地点、B地点）を求める
  var adjust;

  if (rvrInWid || rvrInWid === 0) {
    // 堤内地描画距離が登録されている場合は登録値を使用する
    adjust = rvrInWid / 1000;
  } else {
    // ・川幅が300m以下の場合、河川両岸から300m延長した地点を描画範囲両端とする。
    // ・川幅が300mを超える場合、河川両岸から川幅分だけ延長した地点を描画範囲両端とする。
    adjust = rvrwid <= minWidth ? minWidth : rvrwid;
  }

  var br1 = bearing(leftBank, rightBank);
  var br2 = bearing(rightBank, leftBank);

  var resP1 = destination(leftBank, adjust, br2, options).geometry;
  // logger.debug("resP1.coordinates[0]:" + resP1.coordinates[0]);
  // logger.debug("resP1.coordinates[1]:" + resP1.coordinates[1]);
  var resP2 = destination(rightBank, adjust, br1, options).geometry;

  // A点取得
  // ・右岸から左岸への方向で、左岸から堤内地描画距離の座標を取得
  var locationA = resP1.coordinates;
  // B点取得
  // ・左岸から右岸への方向で、右岸から堤内地描画距離の座標を取得
  var locationB = resP2.coordinates;

  return {
    rvrwid: rvrwid,
    locationA: [locationA[1], locationA[0]],
    locationB: [locationB[1], locationB[0]],
  };
}

/**
 * A点B点取得
 * @param {*} riverInfo
 * @param {*} riverMinWidth
 */
function getEndPoint(riverInfo, riverMinWidth) {
  var width = riverInfo.rvrwid;

  var p1 = [riverInfo.leftBankInfo.lon, riverInfo.leftBankInfo.lat];
  var p2 = [riverInfo.rightBankInfo.lon, riverInfo.rightBankInfo.lat];

  try {
    // 堤内地描画距離を決める閾値(デフォルト値300)
    var dis = riverInfo.rvrwidCfg4 || 300;
    // 閾値
    var border = riverMinWidth || riverInfo.rvrwidCfg3 || 300;
    var option = {
      units: "kilometers",
    };

    // 川幅が閾値以上の場合は、川幅分だけ堤内地を描画
    // 川幅が閾値以下の場合は、rvrwidCfg4だけ堤内地を描画
    if (width > border) {
      dis = width;
    }

    var br1 = bearing(p1, p2);
    var br2 = bearing(p2, p1);
    var resP1 = destination(p1, dis / 1000, br2, option).geometry;
    var resP2 = destination(p2, dis / 1000, br1, option).geometry;

    var start = resP1.coordinates;
    var end = resP2.coordinates;

    var arr = [
      {
        lat: start[1],
        lng: start[0],
      },
      {
        lat: end[1],
        lng: end[0],
      },
    ];

    if (!arr || arr.length < 2)
      throw new Error(getErrorMsg(riverInfo.sys, "ER001"));

    return arr;
  } catch (err) {
    throw new Error(getErrorMsg(riverInfo.sys, "ER001"));
  }
}

/**
 * 現在地の描画確認
 * @param {*} pointA
 * @param {*} pointB
 * @param {*} pointP
 */
function checkPresent(pointA, pointB, pointP) {
  // A↔P間、B↔P間の距離を取得
  var disAP = distance(pointA, pointP, this.option);
  var disBP = distance(pointB, pointP, this.option);

  // 近い点、遠い点を保持
  var pointNearby;
  var pointFar;
  if (disAP < disBP) {
    pointNearby = pointA;
    pointFar = pointB;
  } else {
    pointNearby = pointB;
    pointFar = pointA;
  }

  // 近い点から遠い点までの方向を取得
  var brNF = bearing(pointNearby, pointFar);
  // 近い点から現在地までの方向を取得
  var brNP = bearing(pointNearby, pointP);

  // それぞれマイナスの場合は360度表記に変換
  if (brNF < 0) {
    brNF += 360;
  }
  if (brNP < 0) {
    brNP += 360;
  }

  // 近→遠への角度をプラスマイナス90度した値を保持
  var brNF1 = brNF + 90;
  var brNF2 = brNF - 90;

  // それぞれ0未満・360度より大きい場合は変換
  if (brNF1 < 0) {
    brNF1 += 360;
  } else if (brNF1 > 360) {
    brNF1 -= 360;
  }
  if (brNF2 < 0) {
    brNF2 += 360;
  } else if (brNF2 < 0) {
    brNF2 -= 360;
  }

  // brNFが90度以下か270度以上の場合と、それ以外の場合で条件が異なる
  if (brNF <= 90 || brNF >= 270) {
    if (brNP > brNF1 && brNP < brNF2) {
      return true;
    } else {
      return false;
    }
  } else {
    if (brNP > brNF1 || brNP < brNF2) {
      return true;
    } else {
      return false;
    }
  }
}

/**
 * APラインと現在地の近接インデックスを返却
 * @param {*} pointP
 * @param {*} points
 */
function getNearestPointIndex(pointP, points) {
  var index = 0;
  var min = 0;
  for (var i = 0; i < points.length; i++) {
    var dis = distance(
      [pointP[1], pointP[0]],
      [points[i].latlng.lng, points[i].latlng.lat],
      options
    );
    if (min == 0 || dis < min) {
      min = dis;
      index = i;
    }
  }
  return index;
}

/**
 * 標高取得地点算出
 * @param {*} points
 * @param {*} present
 * @param {*} riverInfo
 * @package {*} makeCrossDatas
 */
function createCossSectionData(points, present, riverInfo, makeCrossData) {
  try {
    if (!riverInfo) throw new Error(getErrorMsg("ph2", "ER002"));

    // ================================================
    // AB間の標高地点(points)を取得

    var from = [points[0].lng, points[0].lat];
    var to = [points[1].lng, points[1].lat];

    var totalDistance = distance(from, to, options);
    var presentDistance = 0;
    var maxPoints = 300;
    if (riverInfo.intervalValue) {
      maxPoints = riverInfo.intervalValue;
    }

    var interval = totalDistance / (maxPoints - 1);

    var data = {
      totalDistance: totalDistance,
      presentDistance: presentDistance,
      points: [],
    };

    //始点を挿入
    data.points.push({
      latlng: points[0],
      distance: 0,
    });

    // 標高取得地点取得
    var cossDatas = getDrawPoints(points, interval);

    cossDatas.forEach(function(d) {
      data.points.push({
        latlng: {
          lat: d.latlng.lat,
          lng: d.latlng.lng,
        },
      });
    });
    cossDatas = null;

    //終点を挿入
    if (data.points.length < maxPoints) {
      data.points.push({
        latlng: points[points.length - 1],
      });
    }

    var self = {};
    self._data = data;
    self.points = points;
    self._reqTileObj = {};

    // 左端右端差し込み処理
    insertBankPoints(riverInfo, self);

    // AB点インデックスを設定（現在地点が無ければ左端・右端）
    riverInfo.AIdx = 0;
    riverInfo.BIdx = data.points.length - 1;

    // ================================================
    // 現在地までをpointに追加
    if (riverInfo.isDispPresent) {
      if (riverInfo.dispP === 1) {
        // A点側にpointsがある場合は、P→A間の後にAB間を追加
        var pointsPA = [
          {
            lat: present.lat,
            lng: present.lng,
          },
          {
            lat: points[0].lat,
            lng: points[0].lng,
          },
        ];
        cossDatas = getDrawPoints(pointsPA, interval);

        var newData = Array();
        cossDatas.forEach(function(d) {
          newData.push({
            latlng: {
              lat: d.latlng.lat,
              lng: d.latlng.lng,
            },
          });
        });
        data.points.forEach(function(d) {
          newData.push(d);
        });

        // AB点インデックスを登録
        riverInfo.AIdx = cossDatas.length;
        riverInfo.BIdx = newData.length - 1;

        // 左右岸堤防高インデックスを現在地点の件数分加算
        riverInfo.leftBankIdx += cossDatas.length;
        riverInfo.rightBankIdx += cossDatas.length;

        // P↔A間の距離を取得
        from = [present.lng, present.lat];
        to = [points[0].lng, points[0].lat];
        presentDistance = distance(from, to, options);

        // 新データに追加
        data = {
          totalDistance: totalDistance,
          presentDistance: presentDistance,
          points: newData,
        };
        self._data = data;
      } else if (riverInfo.dispP === 2) {
        // B点側に現在地がある場合はpointsに追加
        var pointsBP = [
          {
            lat: points[1].lat,
            lng: points[1].lng,
          },
          {
            lat: present.lat,
            lng: present.lng,
          },
        ];

        cossDatas = getDrawPoints(pointsBP, interval);

        cossDatas.forEach(function(d) {
          data.points.push({
            latlng: {
              lat: d.latlng.lat,
              lng: d.latlng.lng,
            },
          });
        });

        // P↔B間の距離を取得
        from = [present.lng, present.lat];
        to = [points[1].lng, points[1].lat];
        data.presentDistance = distance(from, to, options);

        // B点側に現在地点がある場合はAB点・左右岸堤防高のインデックスは変更なし
      }
      cossDatas = null;
      if (!riverInfo.isPresentOut) {
        var nIdx = getNearestPointIndex(riverInfo.pointP, self._data.points);
        riverInfo.nearIndex = nIdx;
      }
    }

    // 全ての標高取得地点を元に、必要なタイル一覧取得
    var zoom = 15;
    getRequestTileObj(self._data.points, zoom, self);

    setOldData(riverInfo);
    self.riverInfo = riverInfo;
    self.makeCrossData = makeCrossData;

    for (var key in self._reqTileObj) {
      //   logger.debug(self._reqTileObj[key].loader);
      dem.load(self._reqTileObj[key].loader, self);
    }
  } catch (err) {
    showErrorMsg(riverInfo, new Error(getErrorMsg(riverInfo.sys, "ER001")));
  }
}

/**
 * 標高取得
 * @param {*} points
 * @param {*} riverInfo
 * @package {*} makeCrossDatas
 */
function getPointEl(points, riverInfo, makeCrossData) {
  try {
    var data = {
      points: [],
    };

    //始点を挿入
    data.points.push({
      latlng: points[0],
      distance: 0,
    });

    var self = {};
    self._data = data;
    self.points = points;
    self._reqTileObj = {};

    // 全ての標高取得地点を元に、必要なタイル一覧取得
    var zoom = 15;
    getRequestTileObj(self._data.points, zoom, self);

    self.riverInfo = riverInfo;
    self.makeCrossData = makeCrossData;

    for (var key in self._reqTileObj) {
      dem.load(self._reqTileObj[key].loader, self);
    }
  } catch (err) {
    showErrorMsg(riverInfo, new Error(getErrorMsg(riverInfo.sys, "ER001")));
  }
}

var _oldRiverInfo_ = {};

function setOldData(riverInfo) {
  _oldRiverInfo_[riverInfo.divId] = riverInfo;
}

/**
 * 全標高取得地点の緯度経度を算出
 * @param {*} points
 * @param {*} interval
 */
function getDrawPoints(points, interval) {
  var retPoints = [];
  var pointA = [points[0].lng, points[0].lat];
  var pointB = [points[1].lng, points[1].lat];

  // AB間の距離
  var distanceAB = distance(pointA, pointB, options);
  // AB間の方向
  var bearingAB = bearing(pointA, pointB);

  var distanceSum = interval;

  // インターバルがAB間距離より大きい場合は空のまま返却
  if (interval > distanceAB) {
    return retPoints;
  }

  // 距離を加算しながら、AB間距離を超えるまでループ
  while (distanceSum <= distanceAB) {
    // AからB方向へインターバル分の地点取得
    var res = destination(pointA, distanceSum, bearingAB, options).geometry;
    var cor = res.coordinates;
    var direct = {
      lat: cor[1],
      lng: cor[0],
    };

    distanceSum += interval;

    retPoints.push({
      latlng: direct,
    });
  }
  return retPoints;
}

/**
 * 左端右端差し込み処理
 * @param {*} riverInfo
 * @param {*} self
 */
function insertBankPoints(riverInfo, self) {
  let points = self._data.points;
  let leftInfo = riverInfo.leftBankInfo;
  let rightInfo = riverInfo.rightBankInfo;
  for (let i = 0; i < points.length - 1; i++) {
    // 左端と重なる・もしくは近似判定
    if (isMatchPoint(points[i].latlng, leftInfo)) {
      riverInfo.leftBankIdx = i;
    } else if (isApproximate(points, i, leftInfo)) {
      self._data.points.splice(i, 0, makeTargetPoint(leftInfo));
      riverInfo.leftBankIdx = i;
      break;
    }
  }

  for (let i = 0; i < points.length - 1; i++) {
    // 右端と重なる・もしくは近似判定
    if (isMatchPoint(points[i].latlng, rightInfo)) {
      riverInfo.rightBankIdx = i;
    } else if (isApproximate(points, i, rightInfo)) {
      self._data.points.splice(i + 1, 0, makeTargetPoint(rightInfo));
      riverInfo.rightBankIdx = i + 1;
      break;
    }
  }
}

/**
 * 標高タイル取得後
 * @param {*} key
 * @param {*} self
 */
function _demLoaded(key, self) {
  var demData = self._reqTileObj[key].loader._demData;
  var pointsData = self.pointsData;
  var data = self._data;

  for (var i = 0; i < data.points.length; i++) {
    if (key != data.points[i].key) continue;

    var idx = data.points[i].px.y * 256 + data.points[i].px.x;

    data.points[i].h = demData ? demData[idx] : 0;
    if (data.points[i].h == null) data.points[i].h = 0;
  }

  for (var i2 = 0; i2 < pointsData.length; i2++) {
    if (key != pointsData[i2].key) continue;

    pointsData[i2].h = demData ? demData[idx] : 0;
    if (pointsData[i2].h == null) pointsData[i2].h = 0;
  }

  self._data = data;
  self._reqTileObj[key].loaded = true;

  _demLoadFinish(self);
}

/**
 * 全ての標高タイル取得後
 * @param {*} self
 */
function _demLoadFinish(self) {
  var loaded = true;
  try {
    var points = self._data.points;

    for (var key in self._reqTileObj) {
      if (!self._reqTileObj[key].loaded) {
        loaded = false;
        break;
      }
    }

    if (!loaded) return;

    self._data.points[0].distance = 0;
    var totalDistance = 0;

    if (!points[0].h) self._data.points[0].h = -9999;

    self._data.max = points[0].h;
    self._data.min = points[0].h;

    for (var i = 0; i < points.length - 1; i++) {
      var dis = distance(
        // [points[i].latlng.lat, points[i].latlng.lng],
        // [points[i + 1].latlng.lat, points[i + 1].latlng.lng],
        [points[i].latlng.lng, points[i].latlng.lat],
        [points[i + 1].latlng.lng, points[i + 1].latlng.lat],
        options
      );

      if (!points[i + 1].h) self._data.points[i + 1].h = -9999;

      totalDistance += dis;
      self._data.points[i + 1].distance = totalDistance;

      if (self._data.max < points[i + 1].h) self._data.max = points[i + 1].h;
      if (points[i + 1].h > -9999 && self._data.min > points[i + 1].h)
        self._data.min = points[i + 1].h;
    }
    self._data.totalDistance = totalDistance;

    if (!self._data.max && self._data.max != 0) {
      logger.error("err");
    } else {
      self.makeCrossData(self);
    }
  } catch (err) {
    showErrorMsg(
      self.riverInfo,
      new Error(getErrorMsg(self.riverInfo.sys, "ER001"))
    );
  }
}

function makeTargetPoint(bankInfo) {
  return {
    latlng: {
      lat: bankInfo.lat,
      lng: bankInfo.lon,
    },
  };
}

function isApproximate(p, i, bankInfo) {
  if (isEmpty(p[i + 1].latlng)) return false;

  var start = [p[0].latlng.lng, p[0].latlng.lat];
  var target = [p[i].latlng.lng, p[i].latlng.lat];
  var riverSide = [bankInfo.lon, bankInfo.lat];
  var next = [p[i + 1].latlng.lng, p[i + 1].latlng.lat];
  var option = {
    units: "kilometers",
  };

  // A点から比較点までの距離
  var firstDis = distance(start, target, option) * 1000;
  // A点から川の端の点までの距離
  var targetDis = distance(start, riverSide, option) * 1000;
  // A点から比較対象点の次の点までの距離
  var nextDis = distance(start, next, option) * 1000;

  // 2点間に川端の点がある場合は新しく挿入させる
  if (firstDis < targetDis && targetDis < nextDis) return true;

  // 同一地点の場合・2点間に川端の点がない場合は挿入しない
  return false;
}

function isMatchPoint(p1, p2) {
  return p1.lat === p2.lat && p1.lng === p2.lon;
}

/**
 * リクエストする標高タイルのタイル座標作成
 * @param {*} points
 * @param {*} zoom
 * @param {*} self
 */
function getRequestTileObj(points, zoom, self) {
  //   logger.debug("getRequestTileObj zoom:" + zoom);
  self.pointsData = [];

  // 各標高取得地点の座標を算出
  // 各座標が存在するタイル座標(tilePoint.x + ':' + tilePoint.y + ':' + zoom)を算出
  for (var i = 0; i < points.length; i++) {
    var p = latLngToPoint(points[i].latlng, zoom);
    var pointLatLng = latLngToPoint(points[i].latlng, zoom);
    var tilePoint = _floor(divideBy(p, 256));
    points[i].key = tilePoint.x + ":" + tilePoint.y + ":" + zoom;
    points[i].px = {
      x: Math.floor(pointLatLng.x - tilePoint.x * 256),
      y: Math.floor(pointLatLng.y - tilePoint.y * 256),
    };

    var pointData = {
      latlng: points[i].latlng,
      z: zoom,
      key: points[i].key,
      px: points[i].px,
    };
    self.pointsData.push(pointData);

    // 同一タイル(pointData.key のタイル座標)がすでに取得されている場合には何もしない
    if (!self._reqTileObj[pointData.key]) {
      // 取得されていない場合は、タイル座標を作成する
      self._reqTileObj[pointData.key] = makeCoordinateInfo(tilePoint, zoom);
      self._reqTileObj[pointData.key].loader.onLoadfunc = _demLoaded.bind(
        this,
        pointData.key,
        self
      );
    }
  }
}

// 座標情報作成
function makeCoordinateInfo(tilePoint, zoom) {
  //   logger.debug("makeCoordinateInfo");
  //   logger.debug("x:" + tilePoint.x + " y:" + tilePoint.y + "z:" + zoom);

  var obj = {
    x: tilePoint.x,
    y: tilePoint.y,
    z: zoom,
    loader: new dem.initialize(tilePoint.x, tilePoint.y, zoom, null, {
      overZooming: true,
    }),
  };
  return obj;
}

var _typeof =
  typeof Symbol === "function" && typeof Symbol.iterator === "symbol"
    ? function(obj) {
        return typeof obj;
      }
    : function(obj) {
        return obj &&
          typeof Symbol === "function" &&
          obj.constructor === Symbol &&
          obj !== Symbol.prototype
          ? "symbol"
          : typeof obj;
      };

// undefined・null・false・空配列・空オブジェクト・空文字・スペースのみはtrue
function isEmpty(value) {
  if (value === undefined) return true;
  if (value === null) return true;
  if (value === false) return true;
  if (value === true) return false;
  if (typeof value === "object") return value.length === 0;
  if (typeof value === "string") return value.replace(/\s+/gi, "").length === 0;
  if (typeof value === "number") return false;
  if (typeof value === "function") return false;
}

var isArray =
  Array.isArray ||
  function(obj) {
    return Object.prototype.toString.call(obj) === "[object Array]";
  };

function convertToPoints(x, y, round) {
  return {
    x: round ? Math.round(x) : x,
    y: round ? Math.round(y) : y,
  };
}

function toPoint(x, y, round) {
  if (isArray(x)) return convertToPoints(x[0], x[1]);
  if (isEmpty(x)) return x;
  if (
    (typeof x === "undefined" ? "undefined" : _typeof(x)) === "object" &&
    "x" in x &&
    "y" in x
  )
    return convertToPoints(x.x, x.y);
  return convertToPoints(x, y, round);
}

function Bounds(a, b) {
  if (!a) return;
  var points = b ? [a, b] : a;
  for (var i = 0, len = points.length; i < len; i++) {
    this.extend(points[i]);
  }
}

Bounds.prototype = {
  extend: function extend(point) {
    point = toPoint(point);

    if (!this.min && !this.max) {
      this.min = convertToPoints(point.x, point.y);
      this.max = convertToPoints(point.x, point.y);
    } else {
      this.min.x = Math.min(point.x, this.min.x);
      this.max.x = Math.max(point.x, this.max.x);
      this.min.y = Math.min(point.y, this.min.y);
      this.max.y = Math.max(point.y, this.max.y);
    }
    return this;
  },
};

function getProjectedPoint(latlng) {
  var d = Math.PI / 180,
    max = 85.0511287798,
    lat = Math.max(Math.min(max, latlng.lat), -max),
    sin = Math.sin(lat * d);

  return convertToPoints(
    6378137 * latlng.lng * d,
    (6378137 * Math.log((1 + sin) / (1 - sin))) / 2
  );
}

function makeSObj() {
  var s = 0.5 / (Math.PI * 6378137);
  return {
    _a: s,
    _b: 0.5,
    _c: -s,
    _d: 0.5,
  };
}

function transform(points, scale) {
  scale = scale || 1;
  var sObj = makeSObj();
  return {
    x: scale * (sObj._a * points.x + sObj._b),
    y: scale * (sObj._c * points.y + sObj._d),
  };
}

function latLngToPoint(latlng, zoom) {
  var projectedPoint = getProjectedPoint(latlng);
  var scale = 256 * Math.pow(2, zoom);
  return transform(projectedPoint, scale);
}

function divideBy(p, num) {
  var point = p;
  convertToPoints(point.x, point.y);
  point.x /= num;
  point.y /= num;
  return point;
}

function _floor(p) {
  p.x = Math.floor(p.x);
  p.y = Math.floor(p.y);
  return p;
}

var errorMess = {
  ph2: {
    /*
        "ER001": "Failed cross-section drawing. Error code: ER001",
        "ER002": "Failed cross-section drawing. Error code: ER002",
        "ER003": "Failed cross-section drawing. Error code: ER003"
        */
    ER001: "横断図の描画に失敗しました。",
    ER002: "河川情報の取得に失敗しました。",
    ER003: "標高データの取得に失敗しました。",
  },
  swin: {
    ER001: "横断図の描画に失敗しました。",
    ER002: "河川情報の取得に失敗しました。",
    ER003: "標高データの取得に失敗しました。",
  },
  allStageNull: "観測値がないため、水位を描画できません",
};

function getErrorMsg(sys, code) {
  return errorMess[sys][code] || "横断図の描画に失敗しました。";
}

// エラーメッセージを表示する
function showErrorMsg(riverInfo, err) {
  logger.error(err);
  logger.dir(riverInfo);
}
